import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  BadgeCheck,
  BadgeEuro,
  BadgePoundSterling,
  Bell,
  ChevronRight,
  BookOpenCheck,
  BookOpenText,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  CreditCard,
  Globe2,
  HandCoins,
  Handshake,
  KeyRound,
  Landmark,
  LayoutDashboard,
  Lock,
  LogOut,
  Mail,
  Menu,
  MessageSquareText,
  Monitor,
  Newspaper,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PackageSearch,
  PhoneCall,
  ReceiptText,
  Search,
  Send,
  ShoppingCart,
  Settings2,
  ShieldCheck,
  Store,
  Sun,
  Target,
  TicketCheck,
  UserRound,
  UserCog,
  Users,
  Workflow,
  WalletCards,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { getThemePreference, setThemePreference } from '../lib/theme';
import WorkspaceZoom from './WorkspaceZoom';
import { isNativeDesktopApp, returnToDesktopLogin } from '../lib/desktop';
import DropdownMenu from './DropdownMenu';
import NotificationCentre from './NotificationCentre';

const sections=[{label:null,items:[{to:'/',label:'Home',icon:LayoutDashboard}]},{label:'Customers',items:[{to:'/customers',label:'Customers',icon:UserRound},{to:'/organisations',label:'Organisations',icon:Building2},{to:'/service-invitations',label:'Service Invitations',icon:Send}]},{label:'Communications',items:[{to:'/inbox',label:'Inbox',icon:Mail},{to:'/whatsapp-inbox',label:'WhatsApp Inbox',icon:MessageSquareText},{to:'/communications/templates',label:'Template Library',icon:BookOpenText},{to:'/communications/publications',label:'Newsletters & Magazine',icon:Newspaper}]},{label:'Sales',items:[{to:'/leads',label:'Leads',icon:Target},{to:'/opportunities',label:'Opportunities',icon:Handshake},{to:'/pipeline',label:'Pipelines',icon:Workflow},{to:'/sales',label:'Sales',icon:ReceiptText}]},{label:'Case Management',items:[{to:'/cases',label:'Cases',icon:TicketCheck},{to:'/integrity',label:'Integrity Reports',icon:ShieldCheck},{to:'/complaints',label:'Complaints',icon:MessageSquareText},{to:'/data-protection',label:'Data Protection',icon:ShieldCheck},{to:'/calls',label:'Calls',icon:PhoneCall},{to:'/back-office',label:'Back Office',icon:UserCog}]},{label:'Services',items:[{to:'/profiles',label:'Sousa Murray Profiles',icon:UserRound},{to:'/mini-websites',label:'Sousa Murray Mini Websites',icon:Globe2}]},{label:'Work',items:[{to:'/tasks',label:'Tasks',icon:ClipboardList},{to:'/meetings',label:'Meetings',icon:CalendarDays}]},{label:'Commercial',items:[{to:'/billing',label:'Billing',icon:BadgePoundSterling},{to:'/collections',label:'Collections & Recoveries',icon:HandCoins},{to:'/studio',label:'Studio Plans & Controls',icon:Globe2}]},{label:'Administration',items:[{to:'/finance',label:'Finance & Stripe',icon:Landmark},{to:'/stripe-operations',label:'Stripe Operations',icon:CreditCard},{to:'/paypal-operations',label:'PayPal Operations',icon:WalletCards},{to:'/profiles/admin',label:'Profiles Admin Centre',icon:ShieldCheck},{to:'/partnerships',label:'Partnerships & Third Parties',icon:Handshake},{to:'/identity',label:'Sousa Murray ID',icon:Lock},{to:'/microsoft365',label:'Microsoft 365',icon:Mail},{to:'/staff-directory',label:'Staff Directory',icon:Users},{to:'/company-id',label:'Company ID',icon:BadgeCheck},{to:'/whatsapp',label:'WhatsApp Settings',icon:Settings2},{to:'/system-configuration',label:'Secrets & Variables',icon:KeyRound},{to:'/website-seos',label:'Website SEOs',icon:Globe2},{to:'/data-integrity',label:'Customer Data Integrity',icon:ShieldCheck},{to:'/settings',label:'CRM Settings',icon:Settings2}]}];

export default function AppShell(){
  const[collapsed,setCollapsed]=useState(false),[query,setQuery]=useState(''),[mobileOpen,setMobileOpen]=useState(false),[results,setResults]=useState([]),[me,setMe]=useState(null),[authError,setAuthError]=useState(''),[themeMode,setThemeMode]=useState(()=>getThemePreference());
  const navigate=useNavigate();
  const location=useLocation();
  useEffect(()=>{setMobileOpen(false);},[location.pathname]);
  useEffect(()=>{if(!mobileOpen)return;const onKeyDown=e=>{if(e.key==='Escape')setMobileOpen(false);};window.addEventListener('keydown',onKeyDown);return()=>window.removeEventListener('keydown',onKeyDown);},[mobileOpen]);
  useEffect(()=>{api('/api/me').then(setMe).catch(e=>setAuthError(e.message));},[]);
  useEffect(()=>{const q=query.trim();if(q.length<2){setResults([]);return;}const controller=new AbortController(),timer=setTimeout(()=>{api(`/api/search?q=${encodeURIComponent(q)}`,{signal:controller.signal}).then(data=>setResults(data.results||[])).catch(()=>setResults([]));},220);return()=>{clearTimeout(timer);controller.abort();};},[query]);
  useEffect(()=>{const changed=event=>setThemeMode(event.detail?.preference||getThemePreference());window.addEventListener('crm-theme-change',changed);return()=>window.removeEventListener('crm-theme-change',changed);},[]);
  const chooseTheme=mode=>{setThemePreference(mode);setThemeMode(mode);};
  const signOut=async()=>{if(isNativeDesktopApp()){try{if(await returnToDesktopLogin())return;}catch{}window.location.assign('/cdn-cgi/access/logout');return;}window.location.assign('/cdn-cgi/access/logout');};
  const openResult=result=>{const route=result.record_type==='customer'?`/customers/${result.id}`:result.record_type==='lead'?'/leads':result.record_type==='deal'?'/opportunities':result.record_type==='organisation'?'/organisations':['shop_order','shop_quote'].includes(result.record_type)?`/orders/${encodeURIComponent(result.id||result.reference||'')}`:'/';navigate(route);setQuery('');};
  const display=me?.staff?.display_name||me?.staff?.email||'Staff',initials=display.split(/\s+/).slice(0,2).map(v=>v[0]).join('').toUpperCase();
  const roleName=authError?'Access not linked':me?.staff?.job_title||me?.roles?.[0]?.name||'Staff';
  const profileHeader=<div className="staff-dropdown-profile"><span className="avatar large">{initials||'SM'}</span><span><strong>{display}</strong><small>{me?.staff?.email||roleName}</small></span></div>;
  const profileGroups=[
    {label:'Account',items:[
      {label:'Profile settings',icon:UserRound,onSelect:()=>navigate('/staff-directory')},
      {label:'My Company ID',icon:BadgeCheck,onSelect:()=>navigate('/my-company-id')},
      {label:'Email preferences',icon:Mail,onSelect:()=>navigate('/settings#email-preferences')},
      {label:'Notifications',icon:Bell,onSelect:()=>navigate('/settings#notifications')},
      {label:'Privacy & security',icon:Lock,onSelect:()=>navigate('/settings#security')},
    ]},
    {label:'Appearance',items:[
      {key:'appearance-light',label:'Light',icon:Sun,selected:themeMode==='light',onSelect:()=>chooseTheme('light')},
      {key:'appearance-dark',label:'Dark',icon:Moon,selected:themeMode==='dark',onSelect:()=>chooseTheme('dark')},
      {key:'appearance-system',label:'System',icon:Monitor,selected:themeMode==='system',onSelect:()=>chooseTheme('system')},
    ]},
    {label:'Customer Operations',items:[
      {label:'Inbox',icon:Mail,onSelect:()=>navigate('/inbox')},
      {label:'WhatsApp Inbox',icon:MessageSquareText,onSelect:()=>navigate('/whatsapp-inbox')},
      {label:'Cases',icon:TicketCheck,onSelect:()=>navigate('/cases')},
      {label:'Data Protection',icon:ShieldCheck,onSelect:()=>navigate('/data-protection')},
      {label:'Template Library',icon:BookOpenText,onSelect:()=>navigate('/communications/templates')},
      {label:'Collections & Recoveries',icon:HandCoins,onSelect:()=>navigate('/collections')},
      {label:'Back Office',icon:UserCog,onSelect:()=>navigate('/back-office')},
      {label:'Finance & Stripe',icon:Landmark,onSelect:()=>navigate('/finance')},
      {label:'Stripe Operations',icon:CreditCard,onSelect:()=>navigate('/stripe-operations')},
      {label:'PayPal Operations',icon:WalletCards,onSelect:()=>navigate('/paypal-operations')},
      {label:'Partnerships & Third Parties',icon:Handshake,onSelect:()=>navigate('/partnerships')},
      {label:'Sousa Murray ID',icon:Lock,onSelect:()=>navigate('/identity')},
      {label:'Microsoft 365',icon:Mail,onSelect:()=>navigate('/microsoft365')},
      {label:'Company ID',icon:BadgeCheck,onSelect:()=>navigate('/company-id')},
      {label:'Secrets & Variables',icon:KeyRound,onSelect:()=>navigate('/system-configuration')},
      {label:'Roles & permissions',icon:Users,onSelect:()=>navigate('/settings#roles')},
      {label:'CRM settings',icon:Settings2,onSelect:()=>navigate('/settings')},
    ]},
    {items:[{label:'Sign out',icon:LogOut,danger:true,onSelect:()=>void signOut()}]},
  ];

  return <div className="app-shell govuk-template__body sm-government-shell">
    <a href="#main-content" className="govuk-skip-link">Skip to main content</a>
    <header className="govuk-template__header sm-government-header">
      <div className="govuk-header sm-government-masthead">
        <div className="govuk-width-container sm-government-header-inner">
          <NavLink className="sm-government-brand" to="/" aria-label="Sousa Murray Customer Operations home">
            <img src="/sousa-murray-icon.svg" width="36" height="36" alt="" aria-hidden="true"/>
            <span><strong>SOUSA MURRAY</strong><small>Customer Operations</small></span>
          </NavLink>
          <div className="sm-government-header-actions">
            <span className="sm-government-private-label">Internal staff service</span>
            <NotificationCentre staff={me?.staff}/>
            <DropdownMenu ariaLabel="Staff account" trigger={<><span className="avatar">{initials||'SM'}</span><span className="profile-copy"><strong>{display}</strong><small>{roleName}</small></span><ChevronDown size={16}/></>} triggerClassName="profile-button profile-menu-trigger" header={profileHeader} groups={profileGroups} width={270}/>
          </div>
        </div>
      </div>
      <section className="govuk-service-navigation sm-government-navigation" aria-label="Service information">
        <div className="govuk-width-container sm-government-service-inner">
          <div className="govuk-service-navigation__container">
            <span className="govuk-service-navigation__service-name">
              <NavLink to="/" className="govuk-service-navigation__link">Customer Operations</NavLink>
            </span>
            <nav className="govuk-service-navigation__wrapper" aria-label="Main service navigation">
              <ul className="govuk-service-navigation__list sm-service-links">
                {[
                  {to:'/',label:'Home'},
                  {to:'/customers',label:'Customers'},
                  {to:'/cases',label:'Case management'},
                  {to:'/inbox',label:'Communications'},
                  {to:'/data-protection',label:'Data protection'},
                  {to:'/billing',label:'Billing'},
                  {to:'/settings',label:'Settings'},
                ].map(item=><li key={item.to} className="govuk-service-navigation__item"><NavLink end={item.to==='/'} to={item.to} className={({isActive})=>'govuk-service-navigation__link '+(isActive?'sm-service-link-current':'')}>{item.label}</NavLink></li>)}
              </ul>
            </nav>
            <button type="button" className="sm-all-services-button" aria-controls="sm-all-services" aria-expanded={mobileOpen} onClick={()=>setMobileOpen(v=>!v)}>
              <Menu size={19} aria-hidden="true"/>{mobileOpen?'Close menu':'All services'}
            </button>
          </div>
        </div>
      </section>
    </header>
    <div id="sm-all-services" className={'sm-services-drawer '+(mobileOpen?'sm-services-drawer-open':'')} hidden={!mobileOpen}>
      <div className="govuk-width-container sm-services-drawer-inner">
        <div className="sm-services-drawer-heading"><h2 className="govuk-heading-m">All Customer Operations services</h2><button className="govuk-button govuk-button--secondary" type="button" onClick={()=>setMobileOpen(false)}>Close menu</button></div>
        <nav aria-label="All Customer Operations services" className="sm-services-groups">
          {sections.map((section,index)=><div className="sm-services-group" key={index}>
            <h3 className="govuk-heading-s">{section.label||'Overview'}</h3>
            <ul className="govuk-list">{section.items.map(({to,label})=><li key={to}><NavLink end={to==='/'} to={to} className="govuk-link" onClick={()=>setMobileOpen(false)}>{label}</NavLink></li>)}</ul>
          </div>)}
        </nav>
      </div>
    </div>
    <div className="govuk-width-container sm-government-width-container">
      <div className="sm-government-utilitybar">
        <div className="global-search-wrap"><Search size={18} aria-hidden="true"/><input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search customer and case records" aria-label="Search Customer Operations"/>{query.trim().length>=2&&<div className="search-results">{results.length?results.map(r=><button key={r.record_type+'-'+r.id} type="button" onClick={()=>openResult(r)}><span className="result-type">{r.record_type}</span><strong>{r.title}</strong><small>{r.reference} · {r.subtitle||r.status||''}</small></button>):<div className="search-empty">No matching records</div>}</div>}</div>
        <WorkspaceZoom/>
      </div>
      <div className="main-column"><main className="content" id="main-content" tabIndex={-1}>
        <Outlet/>
      </main></div>
    </div>
    <footer className="govuk-template__footer sm-government-footer">
      <div className="govuk-footer">
        <div className="govuk-width-container sm-government-footer-inner">
          <div><strong>Sousa Murray Group Ltd</strong><p className="govuk-body-s">Customer Operations · Internal staff service</p></div>
          <div><p className="govuk-body-s">For authorised staff only. Access and actions may be audited.</p></div>
        </div>
      </div>
    </footer>
  </div>;
}
